import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface RealPlayer {
  id: string;
  full_name: string;
  email: string;
  location: string;
  preferred_sports: string[];
  skill_level: string;
  avatar_url?: string;
}

export function useAllPlayers() {
  const [players, setPlayers] = useState<RealPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user: authUser } = useAuth();

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query to exclude current user
      // Note: RLS policy now checks has_role(user_id, 'player') automatically
      let query = supabase
        .from('profiles')
        .select('id, user_id, full_name, email, location, preferred_sports, skill_level, avatar_url')
        .not('full_name', 'is', null)
        .not('email', 'like', 'manual-%@placeholder.racketplaygrid.com');

      // Exclude current user if authenticated
      if (authUser?.id) {
        query = query.neq('user_id', authUser.id);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      const mappedPlayers: RealPlayer[] = data?.map(profile => ({
        id: profile.user_id,
        full_name: profile.full_name || 'Usuario',
        email: profile.email || '',
        location: profile.location || 'México',
        preferred_sports: profile.preferred_sports || [],
        skill_level: profile.skill_level || 'Principiante',
        avatar_url: profile.avatar_url
      })) || [];

      setPlayers(mappedPlayers);
    } catch (err) {
      console.error('Error fetching players:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, [authUser?.id]); // Re-fetch when user changes

  const searchPlayers = (query: string, sport?: string) => {
    return players.filter(player => {
      const matchesSearch = !query || 
        player.full_name.toLowerCase().includes(query.toLowerCase()) ||
        player.email.toLowerCase().includes(query.toLowerCase()) ||
        player.location.toLowerCase().includes(query.toLowerCase()) ||
        player.id.toLowerCase().includes(query.toLowerCase());
      
      const matchesSport = !sport || sport === "" || sport === "todos" || 
        player.preferred_sports.some(s => s.toLowerCase() === sport.toLowerCase());
      
      return matchesSearch && matchesSport;
    });
  };

  return {
    players,
    loading,
    error,
    searchPlayers,
    refetch: fetchPlayers
  };
}